import { collection, doc, getDocs } from 'firebase/firestore/lite';
import { useEffect, useState } from 'react';
import { db } from 'senf-shared';

interface FirestoreCategory {
  id: string;
  name: string;
  title: {
    de: string;
    en: string;
  };
  index: number;
}

export interface FirestoreModel {
  id: string;
  title: {
    de: string;
    en: string;
  };
  prompt: string;
  keywords: {
    de: string[];
    en: string[];
  };
  imgURL: string | null;
  category: string;
  categoryTitle?: {
    de: string;
    en: string;
  };
  categoryIndex?: number;
}

export const useModels = () => {
  const [models, setModels] = useState<FirestoreModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const utopiaDoc = doc(db, 'utopia', 'general');
        
        // Fetch categories first and sort them by index
        const categoriesCollection = collection(utopiaDoc, 'categories');
        const categoriesSnapshot = await getDocs(categoriesCollection);
        const categories = categoriesSnapshot.docs
          .map(doc => ({
            ...doc.data(),
            id: doc.id
          })) as FirestoreCategory[];
        
        // Sort categories by index
        categories.sort((a, b) => a.index - b.index);

        // Then fetch models
        const modelsCollection = collection(utopiaDoc, 'models');
        const modelsSnapshot = await getDocs(modelsCollection);
        
        const modelsList = modelsSnapshot.docs.map(doc => {
          const modelData = doc.data() as FirestoreModel;
          const category = categories.find(cat => cat.id === modelData.category);
          
          return {
            ...modelData,
            id: doc.id,
            categoryTitle: category?.title,
            categoryIndex: category?.index ?? Number.MAX_SAFE_INTEGER // Use MAX_SAFE_INTEGER for models with unknown categories
          };
        });

        // Sort models by category index
        modelsList.sort((a, b) => (a.categoryIndex ?? 0) - (b.categoryIndex ?? 0));

        setModels(modelsList);
        setLoading(false);
      } catch (err) {
        setError(err as Error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { models, loading, error };
}; 